import * as React from 'react';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import { DriverRegistrationPortalNoAuthService } from '@gen/driverRegistration/driverRegistrationPortalNoAuth';
import {
  GetVehicleModelsByManufacturerRequest,
  VehicleTypeModeSelection,
} from '@gen/driverRegistration/driverRegistrationPortalNoAuth/apis';
import { FieldLabel } from './FieldLabel';
import ErrorHint from '../common/ErrorHint';
import FieldDescription from '../common/FieldDescription';
import { useSelector } from '../../redux/store';
import { getFieldError } from '../../redux/notification/notificationsSelectors';
import { Choice, customStyles, errorStyles } from './SelectCommon';
import driverRegistrationApiClient from '../../api/DriverRegistrationApi';
import { getTranslations } from '../../redux/form/formSelectors';

interface CarModelIdProps {
  field: DriverRegistrationPortalNoAuthService.FieldResponse;
  updateHandler: (
    changed: DriverRegistrationPortalNoAuthService.Field['current_value']
  ) => void;
  blurHandler: (
    changed: DriverRegistrationPortalNoAuthService.Field['current_value']
  ) => void;
}

enum UserSelectedVehicleType {
  ALL_VEHICLES,
  CARS_ONLY,
  MOTORBIKES_ONLY,
}

const isChoice = (v: unknown): v is Choice => {
  return !!v && (v as Choice).value !== undefined;
};

const DisablingModeSelectTypes: VehicleTypeModeSelection[] = [
  VehicleTypeModeSelection.CARS_ONLY,
  VehicleTypeModeSelection.MOTORBIKES_ONLY,
];
export function CarModelId({
  field,
  updateHandler,
  blurHandler,
}: CarModelIdProps): React.ReactElement {
  const commonTranslations = useSelector(getTranslations);
  const error = useSelector(getFieldError(field.name));
  const { label, description } = field.translations;
  const mode = field.vehicle_type_mode ?? VehicleTypeModeSelection.HIDDEN;
  const showVehicleTypeDropdown = mode === VehicleTypeModeSelection.USER_SELECT;
  const disableModeSelect = DisablingModeSelectTypes.includes(mode);

  const VehicleTypeOptions = React.useMemo(
    () => [
      {
        title: commonTranslations.vehicle_types.all,
        value: UserSelectedVehicleType.ALL_VEHICLES,
      },
      {
        title: commonTranslations.vehicle_types.cars,
        value: UserSelectedVehicleType.CARS_ONLY,
      },
      {
        title: commonTranslations.vehicle_types.motorbikes,
        value: UserSelectedVehicleType.MOTORBIKES_ONLY,
      },
    ],
    [commonTranslations]
  );

  // Extract backend-provided initial data for manufacturer/model pre-selection
  const initialManufacturerList = field.manufacturer_list;
  const initialManufacturerId = field.current_manufacturer_id;

  let initialVehicleType = VehicleTypeOptions[0];
  if (mode === VehicleTypeModeSelection.CARS_ONLY) {
    initialVehicleType = VehicleTypeOptions[1];
  } else if (mode === VehicleTypeModeSelection.MOTORBIKES_ONLY) {
    initialVehicleType = VehicleTypeOptions[2];
  }

  const [vehicleType, setVehicleType] =
    React.useState<Choice>(initialVehicleType);
  const initialManufacturer =
    initialManufacturerList?.find((m) => m.value === initialManufacturerId) ??
    null;

  const [manufacturer, setManufacturer] = React.useState<Choice | null>(
    initialManufacturer
  );
  const [model, setModel] = React.useState<Choice | null>(null);

  const [manufacturerOptions, setManufacturerOptions] = React.useState<
    Choice[]
  >(() => initialManufacturerList ?? []);
  const [modelOptions, setModelOptions] = React.useState<Choice[]>([]);
  const shouldSkipManufacturerFetch = React.useRef(!!initialManufacturerList);
  const hasPreselectedModel = React.useRef(false);
  const initialCurrentValue = React.useRef(field.current_value);

  const showManufacturerDropDown = React.useMemo(() => {
    return !!vehicleType;
  }, [vehicleType]);

  const showModelSelectDropDown = React.useMemo(() => {
    return !!manufacturer;
  }, [manufacturer]);

  // Load manufacturer options when vehicle type changes
  React.useEffect(() => {
    if (!vehicleType) {
      setManufacturerOptions([]);
      return;
    }

    // Skip fetch on first run if initial data was provided via field props
    if (shouldSkipManufacturerFetch.current) {
      shouldSkipManufacturerFetch.current = false;
      return;
    }

    const fetchData = async (
      payload: DriverRegistrationPortalNoAuthService.GetVehicleMakesRequest
    ) => {
      const response = await driverRegistrationApiClient.getVehicleMakes(
        payload
      );
      setManufacturerOptions(response.list);
    };

    const payload: DriverRegistrationPortalNoAuthService.GetVehicleMakesRequest =
      {};
    if (vehicleType?.value === UserSelectedVehicleType.CARS_ONLY) {
      payload.vehicle_type =
        DriverRegistrationPortalNoAuthService.CarModelVehicleType.CAR;
    } else if (vehicleType?.value === UserSelectedVehicleType.MOTORBIKES_ONLY) {
      payload.vehicle_type =
        DriverRegistrationPortalNoAuthService.CarModelVehicleType.MOTORBIKE;
    }

    fetchData(payload);
  }, [vehicleType]);

  // Load model options when manufacturer changes
  React.useEffect(() => {
    if (!manufacturer) {
      setModelOptions([]);
      return;
    }

    const fetchData = async (
      payload: GetVehicleModelsByManufacturerRequest
    ) => {
      const response =
        await driverRegistrationApiClient.getVehicleModelsByManufacturer(
          payload
        );
      setModelOptions(response.list);
      // Pre-select model matching current_value on initial load
      if (!hasPreselectedModel.current && initialCurrentValue.current != null) {
        hasPreselectedModel.current = true;
        const currentModel = response.list.find(
          (m) => m.value === initialCurrentValue.current
        );
        if (currentModel) {
          setModel(currentModel);
        }
      }
    };

    const payload: GetVehicleModelsByManufacturerRequest = {
      manufacturerId: manufacturer.value as number,
    };

    switch (vehicleType?.value) {
      case UserSelectedVehicleType.CARS_ONLY:
        payload.vehicle_type =
          DriverRegistrationPortalNoAuthService.CarModelVehicleType.CAR;
        break;
      case UserSelectedVehicleType.MOTORBIKES_ONLY:
        payload.vehicle_type =
          DriverRegistrationPortalNoAuthService.CarModelVehicleType.MOTORBIKE;
        break;
      default:
      // No-op, leave as undefined
    }

    fetchData(payload);
  }, [manufacturer, vehicleType]);

  const handleVehicleTypeChange = React.useCallback(
    (option: MultiValue<Choice> | SingleValue<Choice>) => {
      if (!isChoice(option)) {
        return;
      }
      if (option === vehicleType) {
        return;
      }
      setVehicleType(option);
      setManufacturerOptions([]);
      setManufacturer(null);
    },
    [vehicleType]
  );

  const handleManufacturerChange = React.useCallback(
    (option: MultiValue<Choice> | SingleValue<Choice>) => {
      if (!isChoice(option)) {
        return;
      }
      if (option === manufacturer) {
        return;
      }
      setManufacturer(option);
      setModelOptions([]);
      updateHandler(null);
      setModel(null);
    },
    [manufacturer, updateHandler]
  );

  const handleModelChange = React.useCallback(
    (option: MultiValue<Choice> | SingleValue<Choice>) => {
      if (!isChoice(option)) {
        return;
      }
      if (option === model) {
        return;
      }
      setModel(option);
      updateHandler(option?.value);
    },
    [model, updateHandler]
  );

  const getOptionLabel = React.useCallback(
    (item: Choice) => item.title?.toString() ?? '',
    []
  );
  const getOptionValue = React.useCallback(
    (item: Choice) => item.value?.toString() ?? '',
    []
  );

  return (
    <>
      <FieldLabel text={label} isRequired={field.is_required} />

      {showVehicleTypeDropdown && (
        <div className="w-full my-2">
          <ReactSelect
            id={field.name}
            name={field.name}
            placeholder={
              commonTranslations.vehicle_types.vehicle_type_placeholder
            }
            value={vehicleType}
            options={VehicleTypeOptions}
            isDisabled={disableModeSelect}
            styles={
              error && !showManufacturerDropDown ? errorStyles : customStyles
            }
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            isSearchable
            // blurInputOnSelect (triggers autosave prematurely)
            closeMenuOnSelect
            onChange={handleVehicleTypeChange}
            menuPlacement="auto"
          />
        </div>
      )}

      {showManufacturerDropDown && (
        <div className="w-full my-2">
          <ReactSelect
            id={field.name}
            name={field.name}
            placeholder={
              commonTranslations.vehicle_types.vehicle_manufacturer_placeholder
            }
            value={manufacturer}
            options={manufacturerOptions}
            styles={
              error && !showModelSelectDropDown ? errorStyles : customStyles
            }
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            isSearchable
            // blurInputOnSelect (triggers autosave prematurely)
            closeMenuOnSelect
            onChange={handleManufacturerChange}
            menuPlacement="auto"
          />
        </div>
      )}

      {showModelSelectDropDown && (
        <div className="w-full my-2">
          <ReactSelect
            id={field.name}
            name={field.name}
            placeholder={
              commonTranslations.vehicle_types.vehicle_model_placeholder
            }
            value={model}
            options={modelOptions}
            styles={error ? errorStyles : customStyles}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            isSearchable
            // blurInputOnSelect (triggers autosave prematurely)
            closeMenuOnSelect
            onChange={handleModelChange}
            onBlur={(e) => {
              blurHandler(e);
            }}
            menuPlacement="auto"
          />
        </div>
      )}

      <ErrorHint error={error} />
      <FieldDescription description={description} />
    </>
  );
}
